import {
  Activity,
  ArrowRight,
  BarChart3,
  Clock,
  Plus,
  Settings,
  Sparkles,
  Target,
  TrendingUp,
  Users,
} from "lucide-react";
import { motion } from "framer-motion";
import React, { useMemo } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  XAxis,
  YAxis,
} from "recharts";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "../../../components/ui/chart";
import { Loader } from "../../../components/ui/loader";
import { useDashboardContext } from "../../../context/DashboardContext";
import "./styles.scss";
import { Link } from "react-router-dom";

const Overview: React.FC = () => {
  const { profile, isLoading } = useDashboardContext();

  // Stats data - clean minimal design
  const stats = useMemo(
    () => [
      {
        title: "Total Events",
        value: "32",
        change: "+20%",
        comparison: "25 last week",
        trend: "up",
      },
      {
        title: "Total Attendees",
        value: "1,284",
        change: "+8.2%",
        comparison: "1,187 last week",
        trend: "up",
      },
      {
        title: "Revenue",
        value: "$12,450",
        change: "+23.1%",
        comparison: "$10,110 last week",
        trend: "up",
      },
      {
        title: "Growth Rate",
        value: "18.4%",
        change: "+4.2%",
        comparison: "14.2% last week",
        trend: "up",
      },
    ],
    []
  );

  // Chart data with more points
  const performanceData = useMemo(
    () => [
      { month: "Jan", events: 8, attendees: 320, revenue: 2400 },
      { month: "Feb", events: 12, attendees: 450, revenue: 3800 },
      { month: "Mar", events: 15, attendees: 580, revenue: 4200 },
      { month: "Apr", events: 18, attendees: 720, revenue: 5100 },
      { month: "May", events: 20, attendees: 890, revenue: 6800 },
      { month: "Jun", events: 24, attendees: 1284, revenue: 12450 },
    ],
    []
  );

  const chartConfig = {
    events: {
      label: "Events",
      color: "#17265a",
    },
    attendees: {
      label: "Attendees",
      color: "#17265a",
    },
    revenue: {
      label: "Revenue",
      color: "#17265a",
    },
  };

  const recentEvents = useMemo(
    () => [
      {
        id: "1",
        title: "Tech Conference 2024",
        date: "2 days ago",
        attendees: 250,
        revenue: 12500,
        status: "completed",
        category: "Technology",
      },
      {
        id: "2",
        title: "Summer Music Festival",
        date: "5 days ago",
        attendees: 1800,
        revenue: 54000,
        status: "completed",
        category: "Entertainment",
      },
      {
        id: "3",
        title: "Business Workshop",
        date: "1 week ago",
        attendees: 95,
        revenue: 4750,
        status: "completed",
        category: "Business",
      },
    ],
    []
  );

  const quickActions = useMemo(
    () => [
      {
        label: "Create Event",
        icon: Plus,
        description: "Launch a new event",
        color: "from-blue-500 to-cyan-500",
        hoverColor: "from-blue-600 to-cyan-600",
        link: "/d/events/new",
      },
      {
        label: "View Analytics",
        icon: BarChart3,
        description: "Deep dive into metrics",
        color: "from-purple-500 to-pink-500",
        hoverColor: "from-purple-600 to-pink-600",
        link: "/d/analytics",
      },
      {
        label: "Manage Events",
        icon: Settings,
        description: "Edit & organize",
        color: "from-emerald-500 to-teal-500",
        hoverColor: "from-emerald-600 to-teal-600",
        link: "/d/events",
      },
    ],
    []
  );

  // Pie chart data for event distribution
  const eventDistribution = [
    { name: "Tech", value: 35, color: "#17265a" },
    { name: "Music", value: 25, color: "#17265a" },
    { name: "Business", value: 20, color: "#17265a" },
    { name: "Sports", value: 15, color: "#17265a" },
    { name: "Other", value: 5, color: "#17265a" },
  ];

  if (isLoading) {
    return (
      <div className="overview-container">
        <div className="overview-loading">
          <Loader size="lg" />
          <p>Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="overview-container">
      {/* Hero Header Section */}
      <motion.div
        initial={{ opacity: 0, y: -30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="overview-hero"
      >
        <div className="overview-hero__content">
          <div>
            <motion.h1
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="overview-hero__title"
            >
              Welcome back, {profile?.first_name || "there"}!{" "}
              <span className="wave">👋</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="overview-hero__subtitle"
            >
              Here's what's happening with your events today.
            </motion.p>
          </div>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4 }}
            className="overview-hero__badge"
          >
            <Sparkles className="h-4 w-4" />
            <span>All systems operational</span>
          </motion.div>
        </div>
      </motion.div>

      {/* Stats Cards Grid */}
      <div className="overview-stats-grid">
        {stats.map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 * index, duration: 0.4 }}
            className="stat-card-wrapper"
          >
            <Card className="stat-card">
              <CardHeader className="stat-card__header">
                <CardTitle className="stat-card__title">{stat.title}</CardTitle>
              </CardHeader>
              <CardContent className="stat-card__content">
                <div className="stat-card__value">{stat.value}</div>
                <div className="stat-card__trend">
                  <div className="stat-card__trend-icon">
                    <TrendingUp className="h-3.5 w-3.5" />
                  </div>
                  <span className="stat-card__change">{stat.change}</span>
                  <span className="stat-card__comparison">
                    {stat.comparison}
                  </span>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Main Content Grid */}
      <div className="overview-main-grid">
        {/* Performance Chart - Large */}
        <motion.div
          initial={{ opacity: 0, x: -40 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.5, duration: 0.6 }}
          className="overview-chart-section"
        >
          <Card className="chart-card">
            <CardHeader className="chart-card__header">
              <div className="chart-card__header-top">
                <div>
                  <CardTitle className="chart-card__title">
                    <Activity className="h-5 w-5" />
                    Performance Overview
                  </CardTitle>
                  <CardDescription>
                    Event metrics over the last 6 months
                  </CardDescription>
                </div>
                <div className="chart-card__badge">
                  <TrendingUp className="h-3.5 w-3.5" />
                  <span>Trending Up</span>
                </div>
              </div>
            </CardHeader>
            <CardContent className="chart-card__content">
              <div className="chart-wrapper">
                <ChartContainer
                  config={chartConfig}
                  className="h-[320px] w-full"
                >
                  <AreaChart
                    data={performanceData}
                    margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                  >
                    <defs>
                      <linearGradient
                        id="gradientEvents"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="0%"
                          stopColor="#17265a"
                          stopOpacity={0.3}
                        />
                        <stop
                          offset="100%"
                          stopColor="#17265a"
                          stopOpacity={0}
                        />
                      </linearGradient>
                      <linearGradient
                        id="gradientAttendees"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="0%"
                          stopColor="#17265a"
                          stopOpacity={0.3}
                        />
                        <stop
                          offset="100%"
                          stopColor="#17265a"
                          stopOpacity={0}
                        />
                      </linearGradient>
                    </defs>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="rgba(0, 0, 0, 0.06)"
                      vertical={false}
                    />
                    <XAxis
                      dataKey="month"
                      tickLine={false}
                      axisLine={false}
                      tick={{ fill: "#6b7280", fontSize: 12, fontWeight: 500 }}
                      style={{ fontSize: "12px" }}
                    />
                    <YAxis
                      tickLine={false}
                      axisLine={false}
                      tick={{ fill: "#6b7280", fontSize: 12, fontWeight: 500 }}
                      style={{ fontSize: "12px" }}
                    />
                    <ChartTooltip
                      content={<ChartTooltipContent indicator="line" />}
                      cursor={{
                        stroke: "rgba(0, 0, 0, 0.1)",
                        strokeWidth: 1,
                        strokeDasharray: "5 5",
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="events"
                      stroke="#17265a"
                      strokeWidth={2.5}
                      fill="url(#gradientEvents)"
                      fillOpacity={1}
                    />
                    <Area
                      type="monotone"
                      dataKey="attendees"
                      stroke="#17265a"
                      strokeWidth={2.5}
                      fill="url(#gradientAttendees)"
                      fillOpacity={1}
                    />
                  </AreaChart>
                </ChartContainer>
              </div>
              <div className="chart-legend">
                <div className="chart-legend-item">
                  <div className="chart-legend-dot chart-legend-dot--events"></div>
                  <span className="chart-legend-label">Events</span>
                  <span className="chart-legend-value">24</span>
                </div>
                <div className="chart-legend-item">
                  <div className="chart-legend-dot chart-legend-dot--attendees"></div>
                  <span className="chart-legend-label">Attendees</span>
                  <span className="chart-legend-value">1,284</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Sidebar with Recent Events & Distribution */}
        <div className="overview-sidebar">
          {/* Recent Events */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.6, duration: 0.6 }}
            className="sidebar-section"
          >
            <Card className="recent-events-card">
              <CardHeader>
                <CardTitle className="recent-events__title">
                  <Clock className="h-5 w-5" />
                  Recent Events
                </CardTitle>
                <CardDescription>Your latest event activity</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="recent-events-list">
                  {recentEvents.map((event, index) => (
                    <motion.div
                      key={event.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.7 + index * 0.1, duration: 0.4 }}
                      whileHover={{ y: -2 }}
                      className="recent-event-card"
                    >
                      <div className="recent-event-card__header">
                        <h4 className="recent-event-card__title">
                          {event.title}
                        </h4>
                        <span className="recent-event-card__badge">
                          {event.status}
                        </span>
                      </div>
                      <div className="recent-event-card__meta">
                        <span className="recent-event-card__meta-item">
                          <Clock className="h-3.5 w-3.5" />
                          {event.date}
                        </span>
                        <span className="recent-event-card__meta-item">
                          <Users className="h-3.5 w-3.5" />
                          {event.attendees.toLocaleString()}
                        </span>
                      </div>
                      <div className="recent-event-card__footer">
                        <span className="recent-event-card__category">
                          {event.category}
                        </span>
                        <span className="recent-event-card__revenue">
                          ${event.revenue.toLocaleString()}
                        </span>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Event Distribution Chart */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.6 }}
            className="sidebar-section"
          >
            <Card className="distribution-card">
              <CardHeader>
                <CardTitle className="distribution-card__title">
                  <Target className="h-5 w-5" />
                  Event Categories
                </CardTitle>
                <CardDescription>Distribution by type</CardDescription>
              </CardHeader>
              <CardContent>
                <ChartContainer config={chartConfig} className="h-[200px]">
                  <PieChart>
                    <Pie
                      data={eventDistribution}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) =>
                        `${name} ${(percent * 100).toFixed(0)}%`
                      }
                      outerRadius={70}
                      fill="#17265a"
                      dataKey="value"
                    >
                      {eventDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <ChartTooltip content={<ChartTooltipContent />} />
                  </PieChart>
                </ChartContainer>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>

      {/* Quick Actions Section */}
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.9, duration: 0.6 }}
        className="overview-actions-section"
      >
        <Card className="actions-card">
          <CardHeader>
            <CardTitle className="actions-card__title">Quick Actions</CardTitle>
            <CardDescription>Get started with common tasks</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="actions-grid">
              {quickActions.map((action, index) => {
                const ActionIcon = action.icon;
                return (
                  <Link to={action.link}>
                    <motion.div
                      key={action.label}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 1 + index * 0.1 }}
                      whileHover={{ scale: 1.05, y: -4 }}
                      whileTap={{ scale: 0.98 }}
                      className={`action-button action-button--${index} bg-gradient-to-br ${action.color} hover:${action.hoverColor}`}
                    >
                      <div className="action-button__content">
                        <div className="action-button__icon-wrapper">
                          <ActionIcon className="h-6 w-6" />
                        </div>
                        <div className="action-button__text">
                          <span className="action-button__label">
                            {action.label}
                          </span>
                          <span className="action-button__description">
                            {action.description}
                          </span>
                        </div>
                        <ArrowRight className="h-5 w-5 action-button__arrow" />
                      </div>
                    </motion.div>
                  </Link>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default Overview;
